import {
  Delivery,
  ForwardedOrderInfo,
  Order,
  OrderClazz,
  OrderLabelResponse,
  OrderListOrder,
  OrderListOrderClazz,
  OrderMonitor,
  OrderSearchStatus,
  OrderUpdateShipmentResponse,
  ParcelScan,
  Region,
} from '../types/orderTypes';
import { queryStringFromObject } from 'utils/urlUtil';
import Fetch from '../utils/fetch';
import { settings } from '../settings';

import dayjs, { Dayjs } from 'dayjs';
import { OrderBundle } from '../pages/oldPlanner/utils/bundleDraft';

const dateFormat = 'YYYY-MM-DD';

/**
 * Fetch orders
 * Fetches everything if there are no props
 * @param startDate Fetch orders from this date
 * @param endDate Fetch orders to this date
 * @param status Only fetch orders with this status
 * @param region Only fetch orders delivered in this region
 * @param searchText Fetch orders containing the text in any field (name, email, phone, address...)
 * @param includeBundleIds Fetch orders bundleId
 */
export async function fetchAllOrdersAsync(
  startDate: Dayjs,
  endDate: Dayjs,
  status: OrderSearchStatus,
  region?: Region,
  searchText?: string,
  includeBundleIds?: boolean
): Promise<OrderClazz[]> {
  const queryParams = {
    start: dayjs(startDate).format(dateFormat),
    end: dayjs(endDate).format(dateFormat),
    status,
    region,
    ...(searchText && { searchText }),
    ...(includeBundleIds && { includeBundleIds }),
  };
  const queryString = queryStringFromObject(queryParams);

  const response = await Fetch.get({
    url: `${settings.newApiUrl}/order${queryString}`,
  });
  const orders: Order[] = await response.json();
  return parseOrderResponse(orders);
}

export async function fetchAllOrdersByExternalIdAsync(
  externalId: string
): Promise<OrderClazz[]> {
  const queryParams = {
    externalId: externalId,
  };
  const queryString = queryStringFromObject(queryParams);

  const response = await Fetch.get({
    url: `${settings.newApiUrl}/order${queryString}`,
  });
  const orders: Order[] = await response.json();
  return parseOrderResponse(orders);
}

export async function fetchAllOrdersAsyncForUseEffect(
  startDate: string,
  endDate: string,
  status: OrderSearchStatus,
  region: Region = undefined
): Promise<OrderClazz[]> {
  return fetchAllOrdersAsync(dayjs(startDate), dayjs(endDate), status, region);
}

export function parseOrderResponse(orders: Order[]): OrderClazz[] {
  if (!orders.length) return [];

  return orders.map(order => new OrderClazz(order));
}

export async function fetchOrdersForMonitor(
  orderIds: string[]
): Promise<OrderMonitor[]> {
  const uniqueOrderIds = Array.from(new Set(orderIds));
  const response = await Fetch.post({
    url: `${settings.newApiUrl}/order/monitor-list`,
    body: {
      orderIds: uniqueOrderIds,
    },
  });
  if (response.ok) {
    return response.json();
  } else {
    const message = `Error fetching orders ${orderIds}`;
    console.warn(message, response);
    throw message;
  }
}

export async function fetchOrdersForOrderListAsync(
  startDate: Dayjs,
  endDate: Dayjs,
  status: OrderSearchStatus,
  includeBundleIds: boolean,
  region: Region = undefined,
  searchText: string = undefined
): Promise<OrderListOrderClazz[]> {
  const queryParams = {
    start: dayjs(startDate).format(dateFormat),
    end: dayjs(endDate).format(dateFormat),
    status,
    region,
    searchText: searchText,
    includeBundleIds,
  };
  const queryString = queryStringFromObject(queryParams);

  const response = await Fetch.get({
    url: `${settings.newApiUrl}/order/overview-list${queryString}`,
  });
  const orderList: OrderListOrder[] = await response.json();
  return parseOrderListResponse(orderList);
}

function parseOrderListResponse(
  orderList: OrderListOrder[]
): OrderListOrderClazz[] {
  if (!orderList.length) {
    return [];
  }
  return orderList.map(order => new OrderListOrderClazz(order));
}

export async function fetchOrders(orderIds: string[]): Promise<Order[]> {
  if (orderIds.length === 0) return [];

  const uniqueOrderIds = Array.from(new Set(orderIds));
  const response = await Fetch.post({
    url: `${settings.newApiUrl}/order/list`,
    body: { orderIds: uniqueOrderIds },
  });

  if (!response.ok) {
    const message = `Error fetching orders ${orderIds}`;
    console.warn(message, response);
    throw message;
  }

  return response.json();
}

export async function fetchOrderListOrdersById(
  orderIds: string[]
): Promise<OrderListOrder[]> {
  if (orderIds.length === 0) return [];

  const uniqueOrderIds = Array.from(new Set(orderIds));
  const response = await Fetch.post({
    url: `${settings.newApiUrl}/order/overview-list`,
    body: { orderIds: uniqueOrderIds },
  });

  if (!response.ok) {
    const message = `Error fetching orders ${orderIds}`;
    console.warn(message, response);
    throw message;
  }

  return response.json();
}

export async function fetchOrder(id: string): Promise<OrderClazz> {
  const order: Order = await Fetch.get({
    url: `${settings.newApiUrl}/order/${id}`,
  })
    .then(response => {
      if (response.status === 200) {
        return response.json();
      }
      throw Error();
    })
    .then(json => {
      return json;
    })
    .catch(error => console.error(`Could not fetch order ${error}`));
  return order ? new OrderClazz(order) : undefined;
}

export async function fetchOrderScans(id: string): Promise<ParcelScan[]> {
  const response = await Fetch.get({
    url: `${settings.newApiUrl}/order/${id}/scans`,
  });
  if (!response.ok) {
    const message = `Could not fetch scans for order id ${id}`;
    console.warn(message, response);
    throw message;
  }
  return response.json();
}

export async function fetchOrdersBundles(
  orderIds: string[]
): Promise<OrderBundle[]> {
  const response = await Fetch.post({
    url: `${settings.newApiUrl}/order/bundles`,
    body: { orderIds: orderIds },
  });

  if (response.ok) {
    const fetchedOrderBundles: OrderBundle[] = await response.json();
    const foundBundles = new Set(
      fetchedOrderBundles
        .filter(orderBundle => orderBundle.bundle) // Temporary till the backend stops sending orders not found
        .map(orderBundle => orderBundle.orderId)
    );
    const missingBundles = orderIds
      .filter(orderId => !foundBundles.has(orderId))
      .map(orderId => {
        return { orderId: orderId, bundle: null };
      });

    return fetchedOrderBundles.concat(missingBundles);
  } else {
    throw Error(
      `Could not get bundles for orders. Response was: ${response.status}`
    );
  }
}

export interface DeliveryInfo {
  delivery: Delivery;
  orderIds: string[];
}

export async function fetchDelivery(deliveryId: string): Promise<DeliveryInfo> {
  const response = await Fetch.get({
    url: `${settings.newApiUrl}/delivery/${deliveryId}`,
  });
  if (response.status !== 200) {
    console.error(`Could not fetch delivery ${deliveryId}`, response);
    return null;
  }
  return await response.json();
}

export async function fetchPutDeliveryFeedbackFlags(
  deliveryToken: string,
  feedbackFlags: string[]
): Promise<boolean> {
  const response = await Fetch.put({
    url: `${settings.newApiUrl}/delivery/token/${deliveryToken}/feedback-flags-change`,
    body: { feedbackFlags: feedbackFlags },
  });
  if (!response.ok) {
    console.error(
      `Could not set feedback flags for delivery ${deliveryToken}`,
      response
    );
  }
  return response.ok;
}

export async function fetchPutSendTrackingMessages(
  orderId: string
): Promise<boolean> {
  const response = await Fetch.put({
    url: `${settings.newApiUrl}/order/${orderId}/send-tracking-messages`,
  });
  if (!response.ok) {
    console.error(
      `Could not trigger sending tracking messages for order ${orderId}`,
      response
    );
  }
  return response.ok;
}

export function setContactlessDelivery(
  deliveryToken: string,
  deliveryPin: string,
  contactless: boolean
): Promise<Response> {
  return Fetch.put({
    url: `${settings.newApiUrl}/delivery/token/${deliveryToken}/confirm-contactless`,
    body: { deliveryPin, contactless },
  });
}

export async function fetchForwardedOrderInfo(
  orderId: string
): Promise<ForwardedOrderInfo> {
  const response = await Fetch.get({
    url: `${settings.newApiUrl}/onehd/order/${orderId}`,
  });
  if (!response.ok) {
    return null;
  }
  return response.json();
}

export async function fetchForwardedOrderLabels(
  orderId: string
): Promise<OrderLabelResponse> {
  const response = await Fetch.get({
    url: `${settings.newApiUrl}/onehd/order/${orderId}/label`,
  });
  if (!response.ok) {
    return null;
  }
  return response.json();
}

export async function confirmOneHDOrderManual(
  orderId: string
): Promise<OrderUpdateShipmentResponse> {
  const response = await Fetch.put({
    url: `${settings.newApiUrl}/onehd/order/${orderId}/confirm-manual`,
  });
  if (!response.ok) {
    return null;
  }
  return response.json();
}
